import {
  Button,
  ButtonProps,
  Input,
  InputProps,
  ThemeUIStyleObject,
} from '@apia/theme';
import { TModify, getSpecificParent, useUpdateEffect } from '@apia/util';
import {
  ChangeEventHandler,
  forwardRef,
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getVariant } from '@apia/theme';
import { Icon, TIconName } from '@apia/icons';

export type TCheckbox = TModify<
  ButtonProps,
  {
    background?: string;
    checked?: boolean;
    color?: string;
    defaultChecked?: boolean;
    icon?: TIconName;
    indeterminate?: boolean;
    native?: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    readOnly?: boolean;
    required?: boolean;
    type?: string;
  }
>;

export const Checkbox = forwardRef<HTMLButtonElement, TCheckbox>(
  (
    {
      disabled,
      icon = 'Check',
      indeterminate,
      name,
      native,
      onChange,
      readOnly,
      required,
      ...props
    },
    ref,
  ) => {
    const [isChecked, setIsChecked] = useState(
      props.defaultChecked ?? props.checked ?? false,
    );

    const checkbox = useRef<HTMLInputElement>(null);

    useUpdateEffect(
      () => setIsChecked(props.checked ?? false),
      [props.checked],
    );

    const sx = useMemo(
      () => ({
        bg: props.background ?? undefined,
        color: props.color ?? undefined,
        '&[aria-checked="false"]': {
          bg: props.background ?? undefined,
          color: 'transparent',
        },
      }),
      [props.background, props.color],
    );

    useEffect(() => {
      if (checkbox.current && indeterminate !== undefined)
        checkbox.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const nativeSx = useMemo(
      () => ({ variant: 'forms.checkbox' }) as ThemeUIStyleObject,
      [],
    );

    return native ? (
      <input
        type="checkbox"
        onClick={props.onClick as unknown as MouseEventHandler}
        aria-checked={isChecked}
        sx={nativeSx}
        ref={checkbox}
        disabled={disabled}
        name={name}
        checked={isChecked}
        onChange={(ev) => {
          if (props.checked === undefined) setIsChecked(ev.target.checked);
          if (onChange) onChange(ev);
        }}
        required={required}
        className={` ${props.className ?? ''} nativeCheckbox `}
        {...(props as unknown as InputProps)}
      />
    ) : (
      <>
        <Button
          {...props}
          onClick={(ev) => {
            if (readOnly) {
              return;
            }
            if (
              getSpecificParent(
                ev.target as HTMLElement,
                (current) => (current as HTMLInputElement).type === 'button',
              )
            ) {
              if (props.onClick) props.onClick(ev);
              checkbox.current?.click();
            }
          }}
          type="button"
          {...getVariant('forms.customCheckbox')}
          role="checkbox"
          aria-checked={isChecked}
          sx={sx}
          ref={ref}
          disabled={disabled}
        >
          <Icon title="" name={icon} size="32px" />
        </Button>
        <Input
          type="checkbox"
          sx={{ display: 'none' }}
          ref={checkbox}
          onClick={(ev) => {
            ev.stopPropagation();
          }}
          name={name}
          checked={isChecked}
          onChange={(ev) => {
            if (props.checked === undefined) setIsChecked(ev.target.checked);
            if (onChange) onChange(ev);
          }}
          required={required}
          disabled={disabled}
        />
      </>
    );
  },
);

Checkbox.displayName = 'Checkbox';
